import { useEffect, useRef, useState } from 'react';
import Loader from './Loader';

/**
 * Immagine con zoom "inscatolato": pinch-to-zoom, pan e doppio tap/click gestiti
 * in JS via Pointer Events e resi con transform CSS dentro un contenitore
 * overflow-hidden. Come per la mappa Leaflet, lo zoom è dell'ELEMENTO, non della
 * pagina: funziona identico in app nativa (dove lo zoom di pagina è bloccato dal
 * viewport) e in webapp (dove lo zoom di pagina resta disponibile fuori dall'immagine).
 *
 * Comportamenti:
 * - pinch con due dita: zoom 1x–4x ancorato al punto di pinch
 * - doppio tap (touch) / doppio click (mouse): alterna 1x ↔ 2.5x sul punto toccato
 * - trascinamento a immagine ingrandita: pan (con un dito o col mouse)
 * - a 1x i gesti a un dito NON vengono intercettati (`touch-action: pan-y`),
 *   così lo scroll verticale della pagina resta naturale
 * - cambio immagine (prop `src`) → zoom azzerato
 */

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const DOUBLE_TAP_SCALE = 2.5;
const DOUBLE_TAP_MS = 300;
const TAP_MOVE_TOLERANCE_PX = 10;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const ZoomableImage = ({ src, alt = '', className = '', zoomSrc = null }) => {
    const containerRef = useRef(null);
    // Puntatori attivi: pointerId → posizione corrente (relativa al centro del contenitore)
    const pointersRef = useRef(new Map());
    // Stato del gesto in corso (pinch o pan), null se nessuno
    const gestureRef = useRef(null);
    // Doppio tap: timestamp e posizione dell'ultimo tap concluso
    const lastTapRef = useRef({ time: 0 });
    // Posizione di pointerdown per distinguere tap da trascinamento
    const downPosRef = useRef(new Map());

    const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });
    const [isGesturing, setIsGesturing] = useState(false);
    // Suggerimento "tocca per zoomare": sparisce al primo zoom e non torna più
    // per la sessione del componente (l'utente ha capito il gesto)
    const [hasZoomed, setHasZoomed] = useState(false);
    const transformRef = useRef(transform);
    transformRef.current = transform;

    // Sorgente effettivamente mostrata: parte dalla variante leggera (`src`) e,
    // al primo zoom, viene promossa a `zoomSrc` (alta risoluzione) appena scaricata
    const [displaySrc, setDisplaySrc] = useState(src);

    // Spinner finché la foto corrente non è scaricata (l'upgrade a zoomSrc invece
    // avviene in background e non lo mostra: c'è già un'immagine a schermo)
    const [isLoading, setIsLoading] = useState(true);

    // Reset al cambio foto
    useEffect(() => {
        setTransform({ scale: 1, x: 0, y: 0 });
        setDisplaySrc(src);
        setIsLoading(true);
    }, [src]);

    // Upgrade progressivo: quando l'utente zooma, precarico la variante ad alta
    // risoluzione e la sostituisco solo a download completato — niente attesa a
    // schermo, la foto si "affina" da sola senza salti di layout
    const isZoomedIn = transform.scale > 1;
    useEffect(() => {
        if (!isZoomedIn || !zoomSrc || displaySrc === zoomSrc) return;
        let cancelled = false;
        const highRes = new Image();
        highRes.onload = () => { if (!cancelled) setDisplaySrc(zoomSrc); };
        highRes.src = zoomSrc;
        return () => { cancelled = true; };
    }, [isZoomedIn, zoomSrc, displaySrc]);

    // Posizione del pointer relativa al centro del contenitore (il transform del
    // figlio non altera il rect del contenitore, che fa da riferimento stabile)
    const getRelativePoint = (e) => {
        const rect = containerRef.current.getBoundingClientRect();
        return {
            x: e.clientX - (rect.left + rect.width / 2),
            y: e.clientY - (rect.top + rect.height / 2),
        };
    };

    // Limita la traslazione così che l'immagine ingrandita non esca mai dai bordi
    const clampTranslation = (t, scale) => {
        const rect = containerRef.current.getBoundingClientRect();
        const maxX = ((scale - 1) * rect.width) / 2;
        const maxY = ((scale - 1) * rect.height) / 2;
        return { x: clamp(t.x, -maxX, maxX), y: clamp(t.y, -maxY, maxY) };
    };

    const applyTransform = (scale, t) => {
        const clamped = scale <= MIN_SCALE ? { x: 0, y: 0 } : clampTranslation(t, scale);
        if (scale > MIN_SCALE) setHasZoomed(true);
        setTransform({ scale, ...clamped });
    };

    const startPinchIfPossible = () => {
        if (pointersRef.current.size !== 2) return;
        const [p1, p2] = [...pointersRef.current.values()];
        const { scale, x, y } = transformRef.current;
        gestureRef.current = {
            type: 'pinch',
            startDist: Math.hypot(p1.x - p2.x, p1.y - p2.y),
            startMid: { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 },
            startScale: scale,
            startT: { x, y },
        };
        setIsGesturing(true);
    };

    const handlePointerDown = (e) => {
        const point = getRelativePoint(e);
        pointersRef.current.set(e.pointerId, point);
        downPosRef.current.set(e.pointerId, point);
        containerRef.current.setPointerCapture?.(e.pointerId);

        if (pointersRef.current.size === 2) {
            startPinchIfPossible();
        } else if (pointersRef.current.size === 1 && transformRef.current.scale > 1) {
            const { x, y } = transformRef.current;
            gestureRef.current = { type: 'pan', startPoint: point, startT: { x, y } };
            setIsGesturing(true);
        }
    };

    const handlePointerMove = (e) => {
        if (!pointersRef.current.has(e.pointerId)) return;
        const point = getRelativePoint(e);
        pointersRef.current.set(e.pointerId, point);

        const gesture = gestureRef.current;
        if (!gesture) return;

        if (gesture.type === 'pinch' && pointersRef.current.size >= 2) {
            const [p1, p2] = [...pointersRef.current.values()];
            const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
            const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
            const newScale = clamp(
                gesture.startScale * (dist / gesture.startDist),
                MIN_SCALE,
                MAX_SCALE
            );
            // Il punto dell'immagine sotto il centro del pinch resta sotto le dita
            const anchor = {
                x: (gesture.startMid.x - gesture.startT.x) / gesture.startScale,
                y: (gesture.startMid.y - gesture.startT.y) / gesture.startScale,
            };
            applyTransform(newScale, {
                x: mid.x - newScale * anchor.x,
                y: mid.y - newScale * anchor.y,
            });
        } else if (gesture.type === 'pan') {
            const { scale } = transformRef.current;
            applyTransform(scale, {
                x: gesture.startT.x + (point.x - gesture.startPoint.x),
                y: gesture.startT.y + (point.y - gesture.startPoint.y),
            });
        }
    };

    const handleDoubleTap = (point) => {
        const { scale, x, y } = transformRef.current;
        if (scale > 1) {
            applyTransform(1, { x: 0, y: 0 });
        } else {
            // Ingrandisce ancorando il punto toccato
            applyTransform(DOUBLE_TAP_SCALE, {
                x: point.x - (DOUBLE_TAP_SCALE / scale) * (point.x - x),
                y: point.y - (DOUBLE_TAP_SCALE / scale) * (point.y - y),
            });
        }
    };

    const handlePointerEnd = (e) => {
        const downPos = downPosRef.current.get(e.pointerId);
        const lastPos = pointersRef.current.get(e.pointerId);
        pointersRef.current.delete(e.pointerId);
        downPosRef.current.delete(e.pointerId);
        containerRef.current?.releasePointerCapture?.(e.pointerId);

        const wasPinch = gestureRef.current?.type === 'pinch';

        // Fine gesto corrente; se resta un dito e siamo ingranditi, passa al pan
        if (pointersRef.current.size === 1 && transformRef.current.scale > 1) {
            const [remaining] = [...pointersRef.current.values()];
            const { x, y } = transformRef.current;
            gestureRef.current = { type: 'pan', startPoint: remaining, startT: { x, y } };
        } else if (pointersRef.current.size === 0) {
            gestureRef.current = null;
            setIsGesturing(false);
        }

        // Riconoscimento tap (niente pinch in corso, dito fermo) → doppio tap/click
        if (e.type === 'pointerup' && !wasPinch && downPos && lastPos) {
            const moved = Math.hypot(lastPos.x - downPos.x, lastPos.y - downPos.y);
            if (moved < TAP_MOVE_TOLERANCE_PX) {
                const now = Date.now();
                if (now - lastTapRef.current.time < DOUBLE_TAP_MS) {
                    lastTapRef.current = { time: 0 };
                    handleDoubleTap(lastPos);
                } else {
                    lastTapRef.current = { time: now };
                }
            }
        }
    };

    return (
        <div
            ref={containerRef}
            // min-h: durante il caricamento l'<img> non ha dimensioni e senza un'altezza
            // minima le miniature sottostanti "salirebbero" attaccate allo spinner
            className={`relative overflow-hidden rounded-xl border border-neutral-300 select-none min-h-[300px] md:min-h-[420px] flex items-center justify-center ${className}`}
            style={{
                // A 1x lasciamo al browser lo scroll verticale (pan-y); ingranditi
                // prendiamo tutti i gesti (none) per gestire il pan senza scrollare la pagina
                touchAction: transform.scale > 1 ? 'none' : 'pan-y',
                cursor: transform.scale > 1 ? 'grab' : 'zoom-in',
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerEnd}
        >
            {
                isLoading &&
                <div className="absolute inset-0 flex items-center justify-center">
                    <Loader />
                </div>
            }

            <img
                src={displaySrc}
                alt={alt}
                draggable={false}
                onLoad={() => setIsLoading(false)}
                onError={() => setIsLoading(false)}
                // Niente rounded sull'immagine: il clipping degli angoli lo fa il
                // contenitore (overflow-hidden + rounded-xl), così la foto arriva
                // a filo del bordo senza spigoli bianchi
                className={`w-full h-full object-contain ${isLoading ? 'opacity-0' : 'opacity-100'}`}
                style={{
                    transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
                    transformOrigin: 'center center',
                    // Fluido su doppio tap e rilascio, immediato durante pinch/pan
                    transition: isGesturing ? 'none' : 'transform 0.2s ease-out',
                    willChange: 'transform',
                }}
            />

            {/* Suggerimento "doppio tap per zoomare": pastiglia al centro della foto.
                L'intera pastiglia fa *tap-tap*, sfuma e riappare (keyframe tap-hint).
                `pointer-events-none` così non intercetta i gesti; al primo zoom
                l'animazione viene tolta e resta il fade-out definitivo. */}
            <div
                aria-hidden="true"
                className={`
                    absolute top-1/2 left-1/2 -ml-5 -mt-5 pointer-events-none
                    w-10 h-10 rounded-full bg-white/85 shadow-md
                    flex items-center justify-center
                    transition-opacity duration-500
                    ${hasZoomed || isLoading ? 'opacity-0' : 'animate-tap-hint'}
                `}
            >
                <i className="fi fi-rr-cursor-finger text-[#225424] text-xl leading-none"></i>
            </div>
        </div>
    );
};

export default ZoomableImage;
